// src/utils/sessionMonitor.js
// Santé de la session côté appareil :
//  - app_session        (info)    1×/session, à la première connexion : base de l'activité quotidienne
//                                  et de la rétention, avec appareil + version (build) + premier passage ou retour
//  - app_version_stale  (warning) l'élève fait tourner une ancienne version alors qu'une nouvelle est en ligne
//  - network_offline    (info)    coupure réseau en cours de session, avec sa durée
import MonitoringService from '../services/MonitoringService';
import { auth } from '../firebaseConfig';
import { deviceInfo, buildId } from './staveRenderMonitor';
import { markStaleVersion, reportPendingReload } from './staleVersion';

const VERSION_CHECK_EVERY_MS = 30 * 60 * 1000;

function safeGet(key) { try { return localStorage.getItem(key); } catch { return null; } }
function safeSet(key, v) { try { localStorage.setItem(key, v); } catch { /* ignore */ } }
function sessionOnce(key) {
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
  } catch { /* ignore */ }
  return true;
}

// 'main.cb0d2156' à partir de '/static/js/main.cb0d2156.js'
export function buildFromManifest(manifest) {
  const f = manifest?.files?.['main.js'];
  return f ? f.split('/').pop().replace(/\.js$/, '') : null;
}

async function checkVersion(startedAt) {
  try {
    const running = buildId();
    if (!running || running === 'dev') return;
    const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
    if (!res.ok) return;
    const latest = buildFromManifest(await res.json());
    if (!latest || latest === running) return;
    markStaleVersion(latest); // rechargement silencieux au prochain changement de page
    if (!sessionOnce('stale-version-logged')) return;
    MonitoringService.warning('app_version_stale', {
      exerciseId: `${running}->${latest}`,
      running,
      latest,
      sessionAgeMin: Math.round((Date.now() - startedAt) / 60000),
      page: window.location.pathname,
      ...deviceInfo(),
    }, auth.currentUser?.uid || null);
  } catch { /* hors ligne ou manifeste absent : rien à signaler */ }
}

export function initSessionMonitor() {
  try {
    const startedAt = Date.now();

    // Résultat d'un rechargement forcé : rapporté par la page rechargée, dès que l'élève est connu
    const unsubscribeReload = auth.onAuthStateChanged((user) => {
      if (!user) return;
      unsubscribeReload();
      reportPendingReload(user.uid);
    });

    // app_session : dès que l'utilisateur est connu, une seule fois par session de navigation
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user || !sessionOnce('app-session-logged')) return;
      const returning = !!safeGet('drumigo-first-seen');
      if (!returning) safeSet('drumigo-first-seen', String(Date.now()));
      MonitoringService.info('app_session', {
        exerciseId: 'session',
        returning,
        daysSinceFirstSeen: returning ? Math.floor((Date.now() - Number(safeGet('drumigo-first-seen'))) / 86400000) : 0,
        landingPage: window.location.pathname,
        ...deviceInfo(),
      }, user.uid);
      unsubscribe();
    });

    // Version périmée : au retour sur l'onglet et toutes les 30 min
    let lastCheck = 0;
    const maybeCheck = () => {
      if (Date.now() - lastCheck < 10 * 60 * 1000) return;
      lastCheck = Date.now();
      checkVersion(startedAt);
    };
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') maybeCheck(); });
    setInterval(maybeCheck, VERSION_CHECK_EVERY_MS);
    setTimeout(maybeCheck, 60 * 1000);

    // Coupures réseau
    let offlineAt = null;
    window.addEventListener('offline', () => { offlineAt = Date.now(); });
    window.addEventListener('online', () => {
      if (!offlineAt) return;
      const offlineMs = Date.now() - offlineAt;
      offlineAt = null;
      if (offlineMs < 3000) return; // micro-coupure sans conséquence
      MonitoringService.info('network_offline', {
        exerciseId: window.location.pathname,
        offlineMs,
        page: window.location.pathname,
        ...deviceInfo(),
      }, auth.currentUser?.uid || null);
    });
  } catch { /* le monitoring ne casse jamais l'app */ }
}
